import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    DndContext,
    DragOverlay,
    closestCorners,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useToast } from '../../contexts/ToastContext';
import KanbanColumn from './KanbanColumn';
import ProductionRunCard from './ProductionRunCard';
import './ProductionKanbanBoard.css';

// Status columns configuration
const KANBAN_COLUMNS = [
    { status: 'planned', label: 'Planned' },
    { status: 'roast', label: 'Roast' },
    { status: 'flavor', label: 'Flavor' },
    { status: 'grind', label: 'Grind' },
    { status: 'brew', label: 'Brew' },
    { status: 'mix', label: 'Mix' },
    { status: 'keg', label: 'Keg' },
    { status: 'can', label: 'Can' },
    { status: 'completed', label: 'Completed' }
];

function isFlavorStageRequired(run) {
    if (!run || typeof run !== 'object') return true;

    const val = run.requires_flavor_stage ?? run.product?.requires_flavor_stage ?? run.requiresFlavorStage;
    if (val === false || val === 'false' || val === 0) {
        return false;
    }
    return true;
}

function getRunTerminalStage(run, formats = []) {
    if (!run) return 'can';
    const outputFormat = typeof run === 'string' ? run : run.output_format;
    const format = formats.find(f => f.id === outputFormat);
    if (format && format.terminal_stage) {
        return format.terminal_stage.toLowerCase();
    }
    
    // Fallback to hardcoded terminal stage mapping
    const TERMINAL_STAGE_BY_FORMAT = {
        cans_rtd:         'can',
        keg_5gal_rtd:     'keg',
        bottle_16oz_conc: 'keg',
        bottle_32oz_conc: 'keg',
        gallon_conc:      'keg',
    };
    return TERMINAL_STAGE_BY_FORMAT[outputFormat] ?? 'can';
}

function isColumnExcluded(columnStatus, run, formats = []) {
    if (!run) return false;

    // Check if run requires flavor stage
    if (columnStatus === 'flavor' && !isFlavorStageRequired(run)) {
        return true;
    }

    const terminalStage = getRunTerminalStage(run, formats);
    
    // Ordered pipeline stages
    const stageOrder = ['planned', 'roast', 'flavor', 'grind', 'brew', 'mix', 'keg', 'can'];
    const termIndex = stageOrder.indexOf(terminalStage);
    const colIndex = stageOrder.indexOf(columnStatus);

    if (termIndex !== -1 && colIndex !== -1 && colIndex > termIndex) {
        return true;
    }

    return false;
}


// Sortable wrapper for ProductionRunCard
function SortableRunCard({ run, onRunStatusUpdated, isDragging, columnStatus, formats, onRefresh }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({
        id: run.id,
        data: {
            type: 'run',
            run: run,
            // handleDragEnd reads over.data.current?.status to find the drop
            // target column. Without this, dropping on an existing card
            // (rather than the empty space around it) resolves `over` to the
            // card's own sortable item instead of its column, and the status
            // read comes back undefined — silently no-opping the drop. Any
            // column with at least one run was affected, not just one stage.
            status: columnStatus,
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isSortableDragging ? 0.5 : 1,
        cursor: 'grab',
        scale: isSortableDragging ? 1.05 : 1,
    };

    return (
        <div 
            ref={setNodeRef} 
            style={style} 
            {...attributes} 
            {...listeners}
            className={`kanban-run-wrapper ${isSortableDragging ? 'dragging' : ''}`}
        >
            <ProductionRunCard
                run={run}
                onRunStatusUpdated={onRunStatusUpdated}
                isDragging={isSortableDragging}
                isKanbanView={true}
                columnStatus={columnStatus}
                formats={formats}
                onRefresh={onRefresh}
            />
        </div>
    );
}

export default function ProductionKanbanBoard({ 
    plans, 
    onUpdateRunStatus,
    formats = [],
    onRefresh
}) {
    const [activeRun, setActiveRun] = useState(null);
    const [overId, setOverId] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [actualQuantity, setActualQuantity] = useState('');
    const [lossReason, setLossReason] = useState('');
    const { showToast } = useToast();

    useEffect(() => {
        if (pendingStatusChange && pendingStatusChange.newStatus === 'completed') {
            const planned = pendingStatusChange.run.planned_quantity ?? pendingStatusChange.run.quantity ?? 0;
            setActualQuantity(String(planned));
            setLossReason('');
        }
    }, [pendingStatusChange]);

    // Configure sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor)
    );

    // Flatten all runs from all plans and group by status
    const runsByStatus = useMemo(() => {
        const grouped = {};
        
        // Initialize all columns
        KANBAN_COLUMNS.forEach(column => {
            grouped[column.status] = [];
        });

        // Group runs by their status
        plans.forEach(plan => {
            const planRuns = plan.production_runs || [];
            planRuns.forEach(run => {
                const runStatus = (run.status || 'planned').toLowerCase();
                if (grouped[runStatus]) {
                    // Add plan info to run for context
                    grouped[runStatus].push({
                        ...run,
                        planId: plan.id,
                        planName: plan.name
                    });
                }
            });
        });

        return grouped;
    }, [plans]);

    // Get total count of all runs
    const totalRuns = useMemo(() => {
        return Object.values(runsByStatus).reduce((sum, runs) => sum + runs.length, 0);
    }, [runsByStatus]);

    const handleDragStart = useCallback((event) => {
        const { active } = event;
        const run = active.data.current?.run;
        if (run) {
            setActiveRun(run);
        }
    }, []);

    const handleDragOver = useCallback((event) => {
        const { over } = event;
        setOverId(over?.id || null);
    }, []);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;

        setActiveRun(null);
        setOverId(null);

        if (!over) return;

        const draggedRun = active.data.current?.run;
        const overColumn = over.data.current?.status;

        if (!draggedRun || !overColumn) return;

        // Block drop if this column is not valid for the run
        if (isColumnExcluded(overColumn, draggedRun, formats)) {
            if (overColumn === 'flavor' && !isFlavorStageRequired(draggedRun)) {
                const runName = draggedRun.flavor || draggedRun.product_name || draggedRun.name || 'This product';
                showToast(`${runName} doesn't have a flavoring stage.`, 'error');
            }
            return;
        }

        const currentStatus = (draggedRun.status || 'planned').toLowerCase();
        const newStatus = overColumn;

        if (currentStatus === newStatus) return;

        setPendingStatusChange({
            run: draggedRun,
            newStatus: newStatus,
            oldStatus: currentStatus
        });
        setShowConfirmModal(true);
    }, [showToast, formats]);

    const handleDragCancel = useCallback(() => {
        setActiveRun(null);
        setOverId(null);
    }, []);

    const handleConfirmStatusChange = useCallback(async () => {
        if (!pendingStatusChange || !onUpdateRunStatus) return;

        const { run, newStatus } = pendingStatusChange;

        try {
            setIsSubmitting(true);
            const options = newStatus === 'completed' ? {
                actualQuantity: actualQuantity !== '' ? Number(actualQuantity) : (run.planned_quantity ?? run.quantity ?? 0),
                lossReason: lossReason
            } : {};
            await onUpdateRunStatus(run, newStatus, options);
        } catch (error) {
            console.error('Failed to update run status:', error);
        } finally {
            setIsSubmitting(false);
            setShowConfirmModal(false);
            setPendingStatusChange(null);
        }
    }, [pendingStatusChange, onUpdateRunStatus, actualQuantity, lossReason]);

    const handleCancelStatusChange = useCallback(() => {
        setShowConfirmModal(false);
        setPendingStatusChange(null);
    }, []);

    // Keyboard shortcuts for modal
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (showConfirmModal) {
                if (e.key === 'Escape') {
                    handleCancelStatusChange();
                } else if (e.key === 'Enter' && !isSubmitting) {
                    handleConfirmStatusChange();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showConfirmModal, isSubmitting, handleCancelStatusChange, handleConfirmStatusChange]);

    const getStatusLabel = (status, run) => {
        if ((status === 'can' || status === 'keg') && run && run.output_format_name) {
            return run.output_format_name;
        }
        const column = KANBAN_COLUMNS.find(col => col.status === status);
        return column ? column.label : status;
    };

    const getColumnLabel = (column, runs, formats) => {
        if (column.status === 'can') {
            const canFormat = formats.find(f => f.terminal_stage?.toLowerCase() === 'can');
            return canFormat ? canFormat.name : 'Cans (RTD)';
        }
        if (column.status === 'keg') {
            if (runs && runs.length > 0) {
                const uniqueNames = [...new Set(runs.map(r => r.output_format_name || r.output_format).filter(Boolean))];
                return uniqueNames.join(', ');
            }
            const kegFormats = formats.filter(f => f.terminal_stage?.toLowerCase() === 'keg');
            if (kegFormats.length > 0) {
                return kegFormats.map(f => f.name).join(' / ');
            }
            return '5-Gallon Keg (RTD)';
        }
        return column.label;
    };


    if (totalRuns === 0) {
        return (
            <div className="kanban-empty-state">
                <div className="empty-state-icon">📋</div>
                <h3>No Production Runs</h3>
                <p>There are currently no production runs to display on the board.</p>
                <p className="empty-state-hint">Production runs will appear here when they are added to plans.</p>
            </div>
        );
    }

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <div className="kanban-board">
                    <div className="kanban-board-header">
                        <div className="board-info">
                            <h2>Production Board</h2>
                            <p>{totalRuns} total run{totalRuns !== 1 ? 's' : ''} across all stages</p>
                        </div>
                        <div className="board-legend">
                            <span className="legend-item">
                                <span className="legend-icon">👆</span>
                                Drag cards to update status
                            </span>
                        </div>
                    </div>

                    <div className="kanban-columns-container">
                        {KANBAN_COLUMNS.map((column) => {
                            const runs = runsByStatus[column.status] || [];
                            const isOver = overId === column.status;
                            const isDisabled = activeRun
                                ? isColumnExcluded(column.status, activeRun, formats)
                                : false;

                            const disabledReason = (isDisabled && column.status === 'flavor' && !isFlavorStageRequired(activeRun))
                                ? 'Flavor stage not required'
                                : (isDisabled && column.status === 'can')
                                    ? 'Format skips canning'
                                    : null;

                            return (
                                <KanbanColumn
                                    key={column.status}
                                    status={column.status}
                                    label={getColumnLabel(column, runs, formats)}
                                    runs={runs}
                                    isOver={isOver}
                                    isDisabled={isDisabled}
                                    disabledReason={disabledReason}
                                >
                                    {runs.map((run) => (
                                        <SortableRunCard
                                            key={run.id}
                                            run={run}
                                            onRunStatusUpdated={onUpdateRunStatus}
                                            columnStatus={column.status}
                                            formats={formats}
                                            onRefresh={onRefresh}
                                        />
                                    ))}
                                </KanbanColumn>
                            );
                        })}
                    </div>

                    <DragOverlay>
                        {activeRun ? (
                            <div className="drag-overlay-card">
                                <ProductionRunCard
                                    run={activeRun}
                                    onRunStatusUpdated={() => {}}
                                    isDragging={true}
                                    isKanbanView={true}
                                    formats={formats}
                                />
                            </div>
                        ) : null}
                    </DragOverlay>
                </div>
            </DndContext>

            {showConfirmModal && pendingStatusChange && createPortal(
                <>
                    <div className="modal-overlay" onClick={handleCancelStatusChange} />
                    <div className="confirmation-modal kanban-modal">
                        <div className="modal-header">
                            <h3>Confirm Status Change</h3>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={handleCancelStatusChange}
                                disabled={isSubmitting}
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="status-change-visual">
                                <div className="status-from">
                                    <span className="status-label">{getStatusLabel(pendingStatusChange.oldStatus, pendingStatusChange.run)}</span>
                                </div>
                                <div className="status-arrow">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <div className="status-to">
                                    <span className="status-label">{getStatusLabel(pendingStatusChange.newStatus, pendingStatusChange.run)}</span>
                                </div>
                            </div>
                            {pendingStatusChange.newStatus === 'completed' ? (
                                <div className="completion-modal-form" style={{ marginTop: '0.75rem' }}>
                                    <p className="completion-question-text" style={{ fontWeight: '700', fontSize: '1rem', color: '#1f2937', marginBottom: '0.375rem' }}>
                                        How many did you actually get?
                                    </p>
                                    <div className="planned-hint" style={{ fontSize: '0.8125rem', color: '#4b5563', marginBottom: '0.625rem', background: '#f3f4f6', padding: '0.375rem 0.625rem', borderRadius: '6px' }}>
                                        Planned Quantity: <strong style={{ color: '#111827' }}>{pendingStatusChange.run.planned_quantity ?? pendingStatusChange.run.quantity ?? 0}</strong> {pendingStatusChange.run.output_unit || 'units'}
                                    </div>
                                    <div className="form-group">
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', color: '#374151', marginBottom: '0.25rem' }}>Actual Final Yield</label>
                                        <input
                                            type="number"
                                            min="0"
                                            className="form-control"
                                            style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
                                            value={actualQuantity}
                                            onChange={(e) => setActualQuantity(e.target.value)}
                                            placeholder="Enter actual final yield..."
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginTop: '0.75rem' }}>
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', color: '#374151', marginBottom: '0.25rem' }}>Loss Reason <span style={{ fontWeight: '400', color: '#6b7280' }}>(Optional)</span></label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.8125rem' }}
                                            value={lossReason}
                                            onChange={(e) => setLossReason(e.target.value)}
                                            placeholder="Explain any production/canning losses (e.g. 15 cans lost during seam calibration)..."
                                        />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p>
                                        You are about to move <strong>{pendingStatusChange.run.flavor || pendingStatusChange.run.name}</strong> from{' '}
                                        <span className="stage-from">{getStatusLabel(pendingStatusChange.oldStatus, pendingStatusChange.run)}</span> to{' '}
                                        <span className="stage-to">{getStatusLabel(pendingStatusChange.newStatus, pendingStatusChange.run)}</span>.
                                    </p>
                                    <p className="modal-subtext">
                                        This will update the production stage for this run.
                                    </p>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="modal-btn modal-btn-cancel"
                                onClick={handleCancelStatusChange}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="modal-btn modal-btn-confirm"
                                onClick={handleConfirmStatusChange}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Completing...' : (pendingStatusChange.newStatus === 'completed' ? 'Complete Run' : 'Confirm')}
                            </button>
                        </div>
                    </div>
                </>,
                document.body
            )}
        </>
    );
}
